import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  ExitToApp as ExitIcon,
  MyLocation as MyLocationIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { interventiService, timbratureService, presenzeService, rapportiniService } from '../services/api';
import RapportinoModal from '../components/RapportinoModal';

dayjs.extend(utc);
dayjs.extend(timezone);

// Componente per mostrare indirizzo GPS
const GPSAddressDisplay = ({ lat, lon, timbratureId, getAddress, distance }) => {
  const [address, setAddress] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const loadAddress = async () => {
      setLoading(true);
      const addr = await getAddress(lat, lon, timbratureId);
      setAddress(addr);
      setLoading(false);
    };
    loadAddress();
  }, [lat, lon, timbratureId]);

  if (loading) {
    return (
      <Typography variant="caption" display="block" sx={{ color: '#757575', mt: 0.5 }}>
        📍 Caricamento indirizzo...
      </Typography>
    );
  }

  if (!address) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" display="block" sx={{ color: '#1976d2', fontWeight: 500 }}>
        📍 {address.street ? `${address.street}, ` : ''}{address.city || 'Località sconosciuta'}
      </Typography>
      {distance !== null && (
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: distance < 0.1 ? '#4caf50' : distance < 0.5 ? '#ff9800' : '#f44336',
            fontWeight: 600,
            mt: 0.3
          }}
        >
          {distance < 0.1
            ? '✓ Al cantiere'
            : `⚠️ Distanza dal cantiere: ${(distance * 1000).toFixed(0)} metri`}
        </Typography>
      )}
    </Box>
  );
};

const Timbrature = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [intervento, setIntervento] = useState(null);
  const [timbrature, setTimbrature] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [presenzeDialogOpen, setPresenzeDialogOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  // Il GPS è un dato IN PIÙ, non una condizione per timbrare: quando non arriva
  // si avvisa e si lascia procedere senza coordinate.
  const [gpsErrore, setGpsErrore] = useState('');
  const [timbraSenzaGps, setTimbraSenzaGps] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [selectedTipo, setSelectedTipo] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [selectedPresenze, setSelectedPresenze] = useState({});
  const [motiviAssenza, setMotiviAssenza] = useState({}); // Motivi individuali per ogni assenza
  const [uscitaAnticipataDialog, setUscitaAnticipataDialog] = useState(false);
  const [orariDialog, setOrariDialog] = useState(false);
  const [selectedPersonaOrari, setSelectedPersonaOrari] = useState(null);
  const [entrataVal, setEntrataVal] = useState(null);
  const [uscitaVal, setUscitaVal] = useState(null);
  const [selectedPersonaUscita, setSelectedPersonaUscita] = useState(null);
  const [motivoUscitaAnticipata, setMotivoUscitaAnticipata] = useState('');
  const [rapportinoModalOpen, setRapportinoModalOpen] = useState(false);
  const [esisteRapportino, setEsisteRapportino] = useState(false);
  const [rapportino, setRapportino] = useState(null);
  const [viewRapportinoDialog, setViewRapportinoDialog] = useState(false);
  const [gpsAddresses, setGpsAddresses] = useState({});
  // #2: bozza "lavori svolti", compilabile durante il lavoro (pre-riempie il rapportino)
  const [descrizioneLavori, setDescrizioneLavori] = useState('');
  const [savingDescrizione, setSavingDescrizione] = useState(false);
  const [descrizioneMsg, setDescrizioneMsg] = useState('');
  // #1: correzione / inserimento manuale timbrature (dimenticate o oltre la mezzanotte)
  const [correzioneOpen, setCorrezioneOpen] = useState(false);
  const [correzioneTipo, setCorrezioneTipo] = useState('');
  const [correzioneOra, setCorrezioneOra] = useState('');
  const [savingCorrezione, setSavingCorrezione] = useState(false);
  const [correzioneMsg, setCorrezioneMsg] = useState('');
  const [correzioneError, setCorrezioneError] = useState('');

  const interventoId = location.state?.interventoId;

  const tipiTimbratura = [
    { id: 'partenza_sede', label: 'Partenza dalla Sede', icon: <ExitIcon />, needsGPS: false, description: 'Per tutto il team', isViaggio: true },
    { id: 'arrivo_cantiere', label: 'Arrivo Cantiere / Inizio Lavoro', icon: <LocationIcon />, needsGPS: true, description: 'Per tutto il team' },
    { id: 'pausa_inizio', label: 'Inizio Pausa', icon: <PauseIcon />, needsGPS: false, description: 'Per tutto il team' },
    { id: 'pausa_fine', label: 'Fine Pausa', icon: <PlayArrowIcon />, needsGPS: false, description: 'Per tutto il team' },
    { id: 'fine_lavoro', label: 'Fine Lavoro', icon: <StopIcon />, needsGPS: true, description: 'Per tutto il team' },
    { id: 'partenza_cantiere', label: 'Partenza dal Cantiere', icon: <ExitIcon />, needsGPS: true, description: 'Per tutto il team', isViaggio: true },
    { id: 'arrivo_sede', label: 'Arrivo in Sede', icon: <CheckCircleIcon />, needsGPS: false, description: 'Per tutto il team', isViaggio: true },
  ];

  useEffect(() => {
    if (!interventoId) {
      navigate('/calendario');
      return;
    }
    loadData();
  }, [interventoId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Carica i dettagli dell'intervento e le timbrature esistenti
      const interventoRes = await interventiService.getById(interventoId);
      setIntervento(interventoRes.data);
      setDescrizioneLavori(interventoRes.data.descrizione_rapportino || ''); // #2

      // Carica timbrature e presenze esistenti
      const timbratureRes = await timbratureService.getByIntervento(interventoId);
      setTimbrature(timbratureRes.data);

      const presenzeRes = await presenzeService.getByIntervento(interventoId);
      setPresenze(presenzeRes.data);

      // Controlla se esiste già un rapportino
      const rapportinoRes = await rapportiniService.getByIntervento(interventoId);
      setEsisteRapportino(!!rapportinoRes.data);
      setRapportino(rapportinoRes.data);

    } catch (error) {
      setError('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const descriviErroreGps = (errore) => {
    if (!errore) return 'Posizione non disponibile.';
    // 1 = permesso negato, 2 = posizione non determinabile, 3 = tempo scaduto
    if (errore.code === 1) return 'Permesso di localizzazione negato.';
    if (errore.code === 3) return 'Il GPS non ha risposto in tempo.';
    return 'Posizione non disponibile (senza rete serve il segnale GPS del telefono).';
  };

  /** Un singolo tentativo. Non lancia MAI: risolve con { posizione, errore }, così
   *  un problema di GPS non può interrompere il flusso della timbratura. */
  const tentaPosizione = (opzioni) =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ posizione: null, errore: 'Geolocalizzazione non supportata dal dispositivo.' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            posizione: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuratezza: pos.coords.accuracy,
            },
            errore: null,
          }),
        (err) => resolve({ posizione: null, errore: descriviErroreGps(err) }),
        opzioni
      );
    });

  /** Due tentativi in sequenza:
   *  1) alta precisione, 25s — sul telefono usa il chip GPS e funziona anche senza
   *     rete, ma "a freddo" (senza assistenza di rete) una prima posizione può
   *     richiedere ben più dei 10s di prima;
   *  2) bassa precisione, 10s — a volte una posizione approssimata si ottiene dove
   *     quella precisa fallisce. */
  const ottieniPosizione = async () => {
    setGpsLoading(true);
    try {
      const preciso = await tentaPosizione({
        enableHighAccuracy: true,
        timeout: 25000,
        maximumAge: 60000,
      });
      if (preciso.posizione) return preciso;

      const approssimato = await tentaPosizione({
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      });
      // se anche il ripiego fallisce riporto l'errore del tentativo principale,
      // che è quello che descrive meglio il motivo
      return approssimato.posizione ? approssimato : preciso;
    } finally {
      setGpsLoading(false);
    }
  };

  const handleOpenTimbratura = (tipo) => {
    setSelectedTipo(tipo);
    setNote('');
    setError('');
    setGpsErrore('');
    setTimbraSenzaGps(false);
    setCurrentPosition(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTipo('');
    setNote('');
    setCurrentPosition(null);
    setGpsErrore('');
    setTimbraSenzaGps(false);
  };

  const handleTimbratura = async () => {
    try {
      const tipoConfig = tipiTimbratura.find(t => t.id === selectedTipo);

      let position = null;
      if (tipoConfig?.needsGPS && !timbraSenzaGps) {
        const esito = await ottieniPosizione();
        position = esito.posizione;
        setCurrentPosition(position);

        if (!position) {
          // Prima l'eccezione del GPS finiva nel catch, che cerca `isOffline`:
          // non trovandolo mostrava un errore generico e la timbratura NON veniva
          // mai inviata, quindi non entrava nemmeno nella coda offline. Ora si
          // avvisa e il clic successivo registra comunque l'orario, senza GPS.
          setGpsErrore(esito.errore || 'Posizione non disponibile.');
          setTimbraSenzaGps(true);
          return;
        }
      }

      // Dati minimi richiesti - ora tutte le timbrature sono per tutto il team
      const data = {
        intervento_id: parseInt(interventoId),
        tipo: selectedTipo,
        // Orario reale dell'azione: viaggia nella coda offline e arriva corretto al replay
        timestamp: new Date().toISOString()
      };

      // Aggiungi campi opzionali solo se presenti
      if (note && note.trim()) {
        data.note = note.trim();
      }

      if (position?.latitude && position?.longitude) {
        data.latitudine = position.latitude;
        data.longitudine = position.longitude;
      }

      console.log('DEBUG Frontend - Sending data:', data); // Debug log

      const response = await timbratureService.create(data);

      // Controlla se c'è un warning sulla distanza
      if (response.data && response.data.warning) {
        alert(response.data.warning);
      }

      handleCloseDialog();

      // Se è una timbratura di fine lavoro e non esiste già un rapportino, apri il modal
      if (selectedTipo === 'fine_lavoro' && !esisteRapportino) {
        setRapportinoModalOpen(true);
      }

      loadData();
    } catch (error) {
      if (error.isOffline) {
        alert('Sei OFFLINE. La timbratura è stata salvata. Puoi procedere.');
        handleCloseDialog();

        // AGGIORNAMENTO OTTIMISTICO UI
        const newTimbratura = {
          id: 'temp-' + Date.now(),
          tipo: selectedTipo,
          data: new Date().toISOString(),
          note: note,
          local: true // Flag per debug visuale se volessimo
        };

        setTimbrature(prev => [...prev, newTimbratura]);

        // Se è fine lavoro, possiamo comunque mostrare il warning rapportino
        if (selectedTipo === 'fine_lavoro' && !esisteRapportino) {
          setRapportinoModalOpen(true);
        }
        return;
      }
      console.error('ERROR Frontend - Timbratura failed:', error); // Debug log
      console.error('ERROR Frontend - Response data:', error.response?.data); // Debug log
      setError(error.response?.data?.message || error.message || 'Errore nella timbratura');
    }
  };

  const handleOpenPresenze = () => {
    // Inizializza la selezione delle presenze con il capocantiere sempre presente
    const initialPresenze = {};
    const initialMotivi = {};

    // Capocantiere sempre presente
    initialPresenze[`capocantiere-${intervento?.capocantiere_id}`] = true;

    // Tecnici - inizializza tutti come presenti
    if (intervento?.tecnici) {
      intervento.tecnici.forEach(tecnico => {
        initialPresenze[`tecnico-${tecnico.id}`] = true;
      });
    }

    setSelectedPresenze(initialPresenze);
    setMotiviAssenza(initialMotivi);
    setPresenzeDialogOpen(true);
  };

  const handlePresenzeChange = (key, isPresent) => {
    setSelectedPresenze(prev => ({
      ...prev,
      [key]: isPresent
    }));

    // Se diventa presente, rimuovi il motivo assenza
    if (isPresent) {
      setMotiviAssenza(prev => {
        const newMotivi = { ...prev };
        delete newMotivi[key];
        return newMotivi;
      });
    }
  };

  const handleMotivoAssenzaChange = (key, motivo) => {
    setMotiviAssenza(prev => ({
      ...prev,
      [key]: motivo
    }));
  };

  const handleSalvaPresenze = async () => {
    try {
      // Prepara i dati per salvare le presenze
      const presenzeData = [];

      // Capocantiere
      const capoKey = `capocantiere-${intervento?.capocantiere_id}`;
      const capoPresente = selectedPresenze[capoKey] !== false;

      presenzeData.push({
        user_id: intervento?.capocantiere_id,
        presente: capoPresente,
        motivo_assenza: !capoPresente ? (motiviAssenza[capoKey] || 'Non specificato') : null,
        note: ''
      });

      // Tecnici
      if (intervento?.tecnici) {
        intervento.tecnici.forEach(tecnico => {
          const tecnicoKey = `tecnico-${tecnico.id}`;
          const isPresente = selectedPresenze[tecnicoKey] !== false;

          presenzeData.push({
            user_id: tecnico.id,
            presente: isPresente,
            motivo_assenza: !isPresente ? (motiviAssenza[tecnicoKey] || 'Non specificato') : null,
            note: ''
          });
        });
      }

      // Invia tutti i dati in un'unica chiamata nel formato che il backend si aspetta
      const requestData = {
        intervento_id: parseInt(interventoId),
        presenze: presenzeData,
        // Orario reale: viaggia nella coda offline e arriva corretto al replay
        timestamp: new Date().toISOString()
      };

      await presenzeService.create(requestData);

      setPresenzeDialogOpen(false);
      loadData(); // Ricarica i dati
    } catch (error) {
      if (error.isOffline) {
        alert('Sei OFFLINE. Le presenze sono state salvate nel telefono. Puoi procedere con il lavoro.');
        setPresenzeDialogOpen(false);

        // AGGIORNAMENTO OTTIMISTICO UI
        // Costruiamo le presenze locali per sbloccare la UI
        const newPresenze = [];

        // Capocantiere
        if (intervento?.capocantiere_id) {
          const capoKey = `capocantiere-${intervento.capocantiere_id}`;
          const capoPresente = selectedPresenze[capoKey] !== false;
          newPresenze.push({
            user_id: intervento.capocantiere_id,
            user_nome: intervento.capocantiere,
            presente: capoPresente,
            motivo_assenza: !capoPresente ? (motiviAssenza[capoKey] || 'Non specificato') : null
          });
        }

        // Tecnici
        if (intervento?.tecnici) {
          intervento.tecnici.forEach(tecnico => {
            const tecnicoKey = `tecnico-${tecnico.id}`;
            const isPresente = selectedPresenze[tecnicoKey] !== false;
            newPresenze.push({
              user_id: tecnico.id,
              user_nome: tecnico.nome,
              presente: isPresente,
              motivo_assenza: !isPresente ? (motiviAssenza[tecnicoKey] || 'Non specificato') : null
            });
          });
        }

        setPresenze(newPresenze);
        return;
      }
      console.error('Error saving presenze:', error);
      setError('Errore nel salvataggio delle presenze');
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere tutte le timbrature duplicate? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      const response = await timbratureService.cleanupDuplicates();
      alert(`${response.data.duplicates_removed} timbrature duplicate rimosse con successo!`);
      loadData(); // Ricarica i dati
    } catch (error) {
      setError('Errore nella pulizia dei duplicati');
    }
  };

  const handleUscitaAnticipata = (persona) => {
    setSelectedPersonaUscita(persona);
    setMotivoUscitaAnticipata('');
    setUscitaAnticipataDialog(true);
  };

  // Punto 5: il capocantiere imposta entrata/uscita individuali di una persona
  const handleOpenOrari = (persona) => {
    setSelectedPersonaOrari(persona);
    setEntrataVal(persona.entrata ? dayjs(persona.entrata) : null);
    setUscitaVal(persona.uscita_anticipata ? dayjs(persona.uscita_anticipata) : null);
    setOrariDialog(true);
  };

  const handleConfirmOrari = async () => {
    try {
      await presenzeService.setOrari(selectedPersonaOrari.id, {
        entrata: entrataVal ? entrataVal.format('HH:mm') : null,
        uscita: uscitaVal ? uscitaVal.format('HH:mm') : null,
      });
      setOrariDialog(false);
      setSelectedPersonaOrari(null);
      loadData();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio degli orari');
    }
  };

  const handleConfirmUscitaAnticipata = async () => {
    try {
      await presenzeService.registraUscitaAnticipata(selectedPersonaUscita.id, {
        motivo_uscita_anticipata: motivoUscitaAnticipata
      });

      alert(`Uscita anticipata registrata per ${selectedPersonaUscita.user_nome}`);
      setUscitaAnticipataDialog(false);
      setSelectedPersonaUscita(null);
      setMotivoUscitaAnticipata('');
      loadData(); // Ricarica i dati
    } catch (error) {
      setError('Errore nella registrazione dell\'uscita anticipata');
    }
  };

  const hasFinelavoro = () => {
    return timbrature.some(t => t.tipo === 'fine_lavoro');
  };

  // Chi può gestire timbrature/rapportino: capocantiere + admin/super_admin (non amministratore/tecnico).
  // La pagina è già scoped a questi ruoli; il backend verifica comunque il capocantiere dell'intervento.
  const canManage = !!user && ['admin', 'super_admin', 'capocantiere'].includes(user.role);

  // #1: il flusso normale delle timbrature è attivo il giorno dell'intervento E il giorno dopo
  // (turni che sforano la mezzanotte). Per le correzioni più vecchie c'è il pannello dedicato.
  const giornoTimbraturaOk = (() => {
    if (!intervento || !intervento.data) return true;
    const di = dayjs(intervento.data).format('YYYY-MM-DD');
    const oggi = dayjs().format('YYYY-MM-DD');
    const ieri = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return di === oggi || di === ieri;
  })();

  // Timbrature ancora non registrate per l'intervento (per la correzione manuale)
  const tipiTimbraturaMancanti = () => {
    let attivi = tipiTimbratura;
    if (intervento && intervento.abilita_timbrature_viaggio === false) {
      attivi = tipiTimbratura.filter((t) => !t.isViaggio);
    }
    const presenti = timbrature.map((t) => t.tipo);
    return attivi.filter((t) => !presenti.includes(t.id));
  };

  // #2: salva la bozza descrizione lavori sull'intervento (poi pre-riempie il rapportino)
  const handleSaveDescrizione = async () => {
    try {
      setSavingDescrizione(true);
      setDescrizioneMsg('');
      await interventiService.updateDescrizioneRapportino(interventoId, { descrizione_rapportino: descrizioneLavori });
      setDescrizioneMsg('Descrizione salvata ✓');
      setIntervento((prev) => (prev ? { ...prev, descrizione_rapportino: descrizioneLavori } : prev));
    } catch (e) {
      setDescrizioneMsg(e.isOffline ? e.message : (e.response?.data?.message || 'Errore nel salvataggio della descrizione'));
    } finally {
      setSavingDescrizione(false);
    }
  };

  // #1: aggiunge manualmente una timbratura mancante con l'orario reale (dimenticata / oltre mezzanotte)
  const handleAddManualTimbratura = async () => {
    setCorrezioneError('');
    setCorrezioneMsg('');
    if (!correzioneTipo) { setCorrezioneError('Seleziona quale timbratura aggiungere.'); return; }
    if (!correzioneOra) { setCorrezioneError('Indica l\'orario reale della timbratura.'); return; }
    try {
      setSavingCorrezione(true);
      // Ancora l'orario al GIORNO DELL'INTERVENTO (ora italiana): così le ore restano corrette anche
      // per gli inserimenti tardivi. La fine oltre la mezzanotte è gestita dal calcolo ore lato server.
      const giorno = dayjs(intervento.data).format('YYYY-MM-DD');
      const ts = dayjs.tz(`${giorno} ${correzioneOra}`, 'Europe/Rome');
      await timbratureService.create({
        intervento_id: interventoId,
        tipo: correzioneTipo,
        timestamp: ts.toISOString(),
        note: 'Inserimento manuale (correzione)',
      });
      setCorrezioneMsg('Timbratura aggiunta ✓');
      setCorrezioneTipo('');
      setCorrezioneOra('');
      await loadData();
    } catch (e) {
      setCorrezioneError(e.isOffline ? e.message : (e.response?.data?.message || 'Errore nell\'inserimento della timbratura'));
    } finally {
      setSavingCorrezione(false);
    }
  };

  // Geocoding inverso: da coordinate a indirizzo
  const getAddressFromCoordinates = async (lat, lon, timbratureId) => {
    if (gpsAddresses[timbratureId]) {
      return gpsAddresses[timbratureId];
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`
      );
      const data = await response.json();

      const address = {
        street: data.address?.road || '',
        city: data.address?.city || data.address?.town || data.address?.village || '',
        postcode: data.address?.postcode || '',
        full: data.display_name || 'Indirizzo non disponibile'
      };

      setGpsAddresses(prev => ({ ...prev, [timbratureId]: address }));
      return address;
    } catch (error) {
      console.error('Errore geocoding:', error);
      return null;
    }
  };

  // Calcola distanza tra due coordinate (formula Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Raggio della Terra in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const getTimbraturaDisponibili = () => {
    // Prima controlla se le presenze sono state registrate
    if (presenze.length === 0) {
      return []; // Nessuna timbratura disponibile se non ci sono presenze registrate
    }

    // Filtra tipi viaggio se disabilitati sull'intervento
    let tipiAttivi = tipiTimbratura;
    if (intervento && intervento.abilita_timbrature_viaggio === false) {
      tipiAttivi = tipiTimbratura.filter(t => !t.isViaggio);
    }

    // Filtra i tipi di timbratura già effettuati oggi
    const tipiGiaEffettuati = timbrature.map(t => t.tipo);

    return tipiAttivi.filter(tipo => {
      // Non permettere timbrature duplicate dello stesso tipo
      if (tipiGiaEffettuati.includes(tipo.id)) {
        return false;
      }

      // Logica sequenziale per le timbrature
      switch (tipo.id) {
        case 'partenza_sede':
          // Sempre disponibile se non già effettuata
          return true;

        case 'arrivo_cantiere':
          // Disponibile dopo la partenza dalla sede, oppure subito se le timbrature
          // viaggio sono disabilitate (squadra già sul posto)
          return tipiGiaEffettuati.includes('partenza_sede') ||
            (intervento && intervento.abilita_timbrature_viaggio === false);

        case 'pausa_inizio':
          // Disponibile solo se si è arrivati in cantiere, non si è in pausa E il lavoro non è ancora finito
          return tipiGiaEffettuati.includes('arrivo_cantiere') &&
            !tipiGiaEffettuati.includes('fine_lavoro') &&
            (!tipiGiaEffettuati.includes('pausa_inizio') ||
              (tipiGiaEffettuati.includes('pausa_inizio') && tipiGiaEffettuati.includes('pausa_fine')));

        case 'pausa_fine':
          // Disponibile solo se si è iniziata una pausa E il lavoro non è ancora finito
          return tipiGiaEffettuati.includes('pausa_inizio') &&
            !tipiGiaEffettuati.includes('fine_lavoro');

        case 'fine_lavoro':
          // Disponibile solo se si è arrivati in cantiere e non si è in pausa
          return tipiGiaEffettuati.includes('arrivo_cantiere') &&
            (!tipiGiaEffettuati.includes('pausa_inizio') ||
              (tipiGiaEffettuati.includes('pausa_inizio') && tipiGiaEffettuati.includes('pausa_fine')));

        case 'partenza_cantiere':
          // Disponibile solo se si è finito di lavorare
          return tipiGiaEffettuati.includes('fine_lavoro');

        case 'arrivo_sede':
          // Disponibile solo se si è partiti dal cantiere
          return tipiGiaEffettuati.includes('partenza_cantiere');

        default:
          return true;
      }
    });
  };

  // Wizard a step: calcola le fasi della giornata e quella attiva, in ordine logico (#8)
  const getWizardInfo = () => {
    const tipiFatti = timbrature.map((t) => t.tipo);
    const viaggio = !(intervento && intervento.abilita_timbrature_viaggio === false);
    const steps = viaggio
      ? [
          { key: 'presenze', label: 'Presenze' },
          { key: 'viaggio', label: 'Viaggio andata' },
          { key: 'lavoro', label: 'Lavoro' },
          { key: 'rapportino', label: 'Rapportino' },
          { key: 'rientro', label: 'Rientro' },
        ]
      : [
          { key: 'presenze', label: 'Presenze' },
          { key: 'lavoro', label: 'Lavoro' },
          { key: 'rapportino', label: 'Rapportino' },
        ];
    let activeKey;
    if (presenze.length === 0) activeKey = 'presenze';
    else if (viaggio && !tipiFatti.includes('arrivo_cantiere')) activeKey = 'viaggio';
    else if (!tipiFatti.includes('fine_lavoro')) activeKey = 'lavoro';
    else if (!esisteRapportino) activeKey = 'rapportino';
    else if (viaggio && !tipiFatti.includes('arrivo_sede')) activeKey = 'rientro';
    else activeKey = 'done';
    const activeStep = activeKey === 'done' ? steps.length : steps.findIndex((s) => s.key === activeKey);
    const hints = {
      presenze: 'Passo 1: registra chi è presente nel team (e le eventuali assenze).',
      viaggio: 'Timbra la partenza dalla sede e l\'arrivo in cantiere (inizio lavoro).',
      lavoro: 'Gestisci le pause e timbra la fine del lavoro quando hai concluso.',
      rapportino: 'Compila il rapportino: le ore sono calcolate automaticamente dalle timbrature.',
      rientro: 'Timbra la partenza dal cantiere e l\'arrivo in sede.',
      done: 'Tutto completato per questo intervento. Ottimo lavoro!',
    };
    return { steps, activeStep, hint: hints[activeKey], done: activeKey === 'done' };
  };

  const getTecniciPresenti = () => {
    // Restituisce la lista dei tecnici presenti selezionati
    const tecniciPresenti = [];

    // Sempre includi il capocantiere (usa capocantiere_id dall'intervento)
    if (intervento?.capocantiere_id) {
      tecniciPresenti.push({
        id: intervento.capocantiere_id,
        nome: intervento.capocantiere,
        ruolo: 'capocantiere'
      });
    }

    // Aggiungi i tecnici selezionati come presenti
    if (intervento?.tecnici && Array.isArray(intervento.tecnici)) {
      intervento.tecnici.forEach(tecnico => {
        // Verifica che il tecnico abbia un ID valido
        if (tecnico && tecnico.id) {
          tecniciPresenti.push({
            id: tecnico.id,
            nome: tecnico.nome,
            ruolo: 'tecnico'
          });
        }
      });
    }

    return tecniciPresenti;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!intervento) {
    return (
      <Container>
        <Alert severity="error">Intervento non trovato</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/calendario')}
          >
            Torna al Calendario
          </Button>
          <Typography variant="h4">
            Gestione Timbrature
          </Typography>
        </Box>

        {/* Pulsante pulizia duplicati solo per amministratori */}
        {(user?.role === 'admin' || user?.role === 'amministratore') && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleCleanupDuplicates}
            startIcon={<CancelIcon />}
          >
            Pulisci Duplicati
          </Button>
        )}
      </Box>

      {/* Informazioni Intervento */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {intervento.cantiere}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Cliente: {intervento.cliente}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Data: {dayjs(intervento.data).format('DD/MM/YYYY')}
            </Typography>
            {intervento.targa_mezzo && (
              <Typography variant="body2" color="text.secondary">
                Targa Mezzo: {intervento.targa_mezzo}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Orario: {intervento.ora_inizio} - {intervento.ora_fine}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Indirizzo: {intervento.cantiere_indirizzo}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Blocco flusso normale se l'intervento non è di oggi né di ieri (turni oltre mezzanotte).
          Le correzioni restano possibili dal pannello "Correggi / completa timbrature" più sotto. */}
      {intervento.data && !giornoTimbraturaOk && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: 2 }}>
          <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon /> Timbrature del giorno non disponibili
          </Typography>
          <Typography variant="body1">
            Il flusso normale è attivo solo nel giorno dell'intervento ({dayjs(intervento.data).format('DD/MM/YYYY')}) e il giorno successivo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Oggi è il {dayjs().format('DD/MM/YYYY')}. Se hai dimenticato una timbratura, usa il pannello
            <strong> "Correggi / completa timbrature" </strong> qui sotto per aggiungerla con l'orario reale.
          </Typography>
        </Paper>
      )}

      {/* Wizard a step: ordine logico della giornata (#8) */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
        {(() => {
          const wiz = getWizardInfo();
          return (
            <>
              <Stepper activeStep={wiz.activeStep} alternativeLabel sx={{ mb: 2 }}>
                {wiz.steps.map((s) => (
                  <Step key={s.key}>
                    <StepLabel>{s.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Alert severity={wiz.done ? 'success' : 'info'} sx={{ borderRadius: 2 }}>
                {wiz.hint}
              </Alert>
            </>
          );
        })()}
      </Paper>

      {/* Informazioni stato timbrature */}
      {giornoTimbraturaOk && (
      <Paper sx={{ p: 2, mb: 3, backgroundColor: presenze.length === 0 ? '#fff3cd' : '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          {presenze.length === 0 ? 'Registrazione Presenze Richiesta' : 'Prossima Timbratura Disponibile'}
        </Typography>
        {presenze.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            ⚠️ Prima di iniziare le timbrature è necessario registrare le presenze del team per oggi.
            Clicca su "Gestisci Presenze" qui sotto.
          </Typography>
        ) : getTimbraturaDisponibili().length > 0 ? (
          <Typography variant="body2" color="text.secondary">
            Puoi effettuare: {getTimbraturaDisponibili().map(t => t.label).join(', ')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Tutte le timbrature sono state completate per oggi.
          </Typography>
        )}
      </Paper>
      )}

      {/* STATO RAPPORTINO - SEMPRE VISIBILE */}
      <Paper
        sx={{
          mb: 3,
          p: 3,
          background: esisteRapportino
            ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
            : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          border: esisteRapportino ? '3px solid #4caf50' : '3px solid #ff9800',
          boxShadow: esisteRapportino
            ? '0 4px 12px rgba(76, 175, 80, 0.3)'
            : '0 6px 16px rgba(255, 152, 0, 0.4)',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          {esisteRapportino ? (
            <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4caf50' }} />
          ) : (
            <DescriptionIcon sx={{ fontSize: '3rem', color: '#ff9800' }} />
          )}
          <Box flex={1}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: esisteRapportino ? '#2e7d32' : '#e65100',
                mb: 0.5
              }}
            >
              {esisteRapportino
                ? '✓ Rapportino Compilato'
                : hasFinelavoro()
                  ? 'Compila il Rapportino'
                  : 'Rapportino non ancora disponibile'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: esisteRapportino ? '#1b5e20' : '#5d4037',
                fontWeight: 500
              }}
            >
              {esisteRapportino
                ? 'Il rapportino di lavoro è stato compilato correttamente.'
                : hasFinelavoro()
                  ? 'Hai timbrato la fine del lavoro: ora puoi compilare il rapportino. Le ore sono calcolate automaticamente dalle timbrature.'
                  : 'Per compilare il rapportino completa prima le timbrature: registra le eventuali pause e uscite anticipate, poi timbra la FINE LAVORO.'}
            </Typography>
          </Box>
        </Box>

        {/* #4: il rapportino si compila SOLO dopo aver timbrato la FINE LAVORO.
            Niente più scorciatoia "ORA": prima si completano le timbrature. */}
        {!esisteRapportino && user?.role !== 'amministratore' && hasFinelavoro() && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="warning"
              size="large"
              startIcon={<DescriptionIcon />}
              onClick={() => setRapportinoModalOpen(true)}
              sx={{
                minWidth: { xs: '100%', sm: '300px' },
                fontWeight: 700,
                py: 2,
                fontSize: '1.1rem',
                boxShadow: '0 6px 16px rgba(255, 152, 0, 0.5)',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(255, 152, 0, 0.6)',
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s',
              }}
            >
              COMPILA RAPPORTINO
            </Button>
          </Box>
        )}

        {/* Quando manca la FINE LAVORO: nessun pulsante, solo l'indicazione di completare le timbrature. */}
        {!esisteRapportino && user?.role !== 'amministratore' && !hasFinelavoro() && (
          <Box mt={2} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100' }}>
              ⏳ Il rapportino sarà compilabile dopo la timbratura di FINE LAVORO.
            </Typography>
          </Box>
        )}

        {/* Pulsante VISUALIZZA se esiste rapportino */}
        {esisteRapportino && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<DescriptionIcon />}
              onClick={() => setViewRapportinoDialog(true)}
              sx={{
                minWidth: { xs: '100%', sm: '300px' },
                fontWeight: 600,
                py: 2,
                fontSize: '1rem',
              }}
            >
              VISUALIZZA RAPPORTINO
            </Button>
          </Box>
        )}
      </Paper>

      {/* #2: Descrizione lavori svolti — compilabile DURANTE il lavoro, senza attendere la fine timbrature */}
      {canManage && !esisteRapportino && presenze.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: '1px solid #90caf9', backgroundColor: '#e3f2fd' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Descrizione lavori svolti</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Puoi scriverla già durante il lavoro: verrà riportata automaticamente nel rapportino.
            Non serve attendere la fine delle timbrature.
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Descrivi i lavori svolti durante l'intervento..."
            value={descrizioneLavori}
            onChange={(e) => setDescrizioneLavori(e.target.value)}
          />
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Button
              variant="contained"
              onClick={handleSaveDescrizione}
              disabled={savingDescrizione}
              startIcon={savingDescrizione ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
            >
              Salva descrizione
            </Button>
            {descrizioneMsg && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: descrizioneMsg.includes('✓') ? '#2e7d32' : '#c62828' }}>
                {descrizioneMsg}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* #1: Correzione / inserimento manuale timbrature (dimenticate o oltre mezzanotte) — sempre disponibile */}
      {canManage && presenze.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: '1px solid #ffcc80' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setCorrezioneOpen((v) => !v)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTimeIcon sx={{ color: '#ef6c00' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Correggi / completa timbrature</Typography>
            </Box>
            <Button size="small">{correzioneOpen ? 'Chiudi' : 'Apri'}</Button>
          </Box>
          {correzioneOpen && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Per una timbratura <strong>dimenticata</strong> o un turno che <strong>sfora la mezzanotte</strong>:
                aggiungi la timbratura mancante indicando l'orario reale. Funziona anche nei giorni successivi —
                l'orario viene registrato sul giorno dell'intervento e le ore si aggiornano da sole.
              </Typography>
              {tipiTimbraturaMancanti().length === 0 ? (
                <Alert severity="success">Tutte le timbrature dell'intervento sono già registrate.</Alert>
              ) : (
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      select
                      fullWidth
                      label="Timbratura mancante"
                      value={correzioneTipo}
                      onChange={(e) => setCorrezioneTipo(e.target.value)}
                    >
                      {tipiTimbraturaMancanti().map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="time"
                      fullWidth
                      label="Orario reale"
                      InputLabelProps={{ shrink: true }}
                      value={correzioneOra}
                      onChange={(e) => setCorrezioneOra(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      color="warning"
                      fullWidth
                      sx={{ height: '56px' }}
                      onClick={handleAddManualTimbratura}
                      disabled={savingCorrezione}
                      startIcon={savingCorrezione ? <CircularProgress size={18} color="inherit" /> : <AccessTimeIcon />}
                    >
                      Aggiungi timbratura
                    </Button>
                  </Grid>
                </Grid>
              )}
              {correzioneError && <Alert severity="error" sx={{ mt: 2 }}>{correzioneError}</Alert>}
              {correzioneMsg && <Alert severity="success" sx={{ mt: 2 }}>{correzioneMsg}</Alert>}
              {timbrature.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">Già registrate:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                    {timbrature.map((t) => {
                      const cfg = tipiTimbratura.find((x) => x.id === t.tipo);
                      return (
                        <Chip
                          key={t.id}
                          size="small"
                          label={`${cfg ? cfg.label : t.tipo} · ${dayjs(t.timestamp).tz('Europe/Rome').format('HH:mm')}`}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Pulsanti Timbratura - solo nel giorno dell'intervento */}
      {giornoTimbraturaOk && (
      <Grid container spacing={2} mb={3}>
        {getTimbraturaDisponibili().map((tipo) => (
          <Grid item xs={12} sm={6} md={4} key={tipo.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {tipo.icon}
                  <Typography variant="h6">
                    {tipo.label}
                  </Typography>
                </Box>
                {tipo.needsGPS && (
                  <Chip
                    size="small"
                    icon={<MyLocationIcon />}
                    label="Richiede GPS"
                    variant="outlined"
                  />
                )}
                <Chip
                  size="small"
                  icon={<GroupIcon />}
                  label="Per tutto il team"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenTimbratura(tipo.id)}
                  startIcon={<AccessTimeIcon />}
                  fullWidth
                >
                  Timbra
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}

      {/* Gestione Presenze */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Gestione Presenze Team
          </Typography>
          {presenze.length === 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GroupIcon />}
              onClick={handleOpenPresenze}
            >
              Gestisci Presenze
            </Button>
          )}
        </Box>

        {/* Lista Team */}
        <Typography variant="subtitle2" gutterBottom>
          Team Assegnato:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={intervento.capocantiere} secondary="Capocantiere" />
            <Box display="flex" alignItems="center" gap={1}>
              {(() => {
                const capoPresenza = presenze.find(p => p.user_id === intervento?.capocantiere_id && p.ruolo === 'capocantiere');
                if (capoPresenza) {
                  if (capoPresenza.uscita_anticipata) {
                    return <Chip label={`Uscita anticipata: ${capoPresenza.motivo_uscita_anticipata}`} color="warning" size="small" />;
                  } else if (capoPresenza.presente) {
                    return (
                      <>
                        <Chip label="Presente" color="success" size="small" />
                        {!hasFinelavoro() && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleUscitaAnticipata(capoPresenza)}
                          >
                            Uscita Anticipata
                          </Button>
                        )}
                      </>
                    );
                  } else {
                    return <Chip label={`Assente: ${capoPresenza.motivo_assenza}`} color="error" size="small" />;
                  }
                }
                return <Chip label="Da verificare" color="warning" size="small" />;
              })()}
              {(() => {
                const cp = presenze.find(p => p.user_id === intervento?.capocantiere_id && p.ruolo === 'capocantiere');
                return cp && cp.presente ? (
                  <Button size="small" variant="text" startIcon={<AccessTimeIcon />} onClick={() => handleOpenOrari(cp)}>
                    Orario
                  </Button>
                ) : null;
              })()}
            </Box>
          </ListItem>
          {intervento.tecnici?.map((tecnico) => (
            <ListItem key={tecnico.id}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary={tecnico.nome} secondary="Tecnico" />
              <Box display="flex" alignItems="center" gap={1}>
                {(() => {
                  const tecnicoPresenza = presenze.find(p => p.user_id === tecnico.id && p.ruolo === 'tecnico');
                  if (tecnicoPresenza) {
                    if (tecnicoPresenza.uscita_anticipata) {
                      return <Chip label={`Uscita anticipata: ${tecnicoPresenza.motivo_uscita_anticipata}`} color="warning" size="small" />;
                    } else if (tecnicoPresenza.presente) {
                      return (
                        <>
                          <Chip label="Presente" color="success" size="small" />
                          {!hasFinelavoro() && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleUscitaAnticipata(tecnicoPresenza)}
                            >
                              Uscita Anticipata
                            </Button>
                          )}
                        </>
                      );
                    } else {
                      return <Chip label={`Assente: ${tecnicoPresenza.motivo_assenza}`} color="error" size="small" />;
                    }
                  }
                  return <Chip label="Da verificare" color="warning" size="small" />;
                })()}
                {(() => {
                  const tp = presenze.find(p => p.user_id === tecnico.id && p.ruolo === 'tecnico');
                  return tp && tp.presente ? (
                    <Button size="small" variant="text" startIcon={<AccessTimeIcon />} onClick={() => handleOpenOrari(tp)}>
                      Orario
                    </Button>
                  ) : null;
                })()}
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Storico Timbrature */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Storico Timbrature Oggi
        </Typography>
        {timbrature.length > 0 ? (
          <List>
            {timbrature.map((timbratura) => {
              const tipoConfig = tipiTimbratura.find(t => t.id === timbratura.tipo);
              const hasGPS = timbratura.latitudine && timbratura.longitudine;

              // Calcola distanza dal cantiere se ha GPS
              let distanceFromSite = null;
              if (hasGPS && intervento?.latitudine && intervento?.longitudine) {
                distanceFromSite = calculateDistance(
                  timbratura.latitudine,
                  timbratura.longitudine,
                  intervento.latitudine,
                  intervento.longitudine
                );
              }

              return (
                <ListItem key={timbratura.id} divider>
                  <ListItemIcon>
                    {tipoConfig?.icon || <AccessTimeIcon />}
                  </ListItemIcon>
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {tipoConfig?.label || timbratura.tipo}
                      </Typography>
                      {timbratura.timbratura_collettiva && (
                        <Chip label="Collettiva" size="small" color="primary" variant="outlined" />
                      )}
                      {hasGPS && (
                        <Chip label="GPS" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" sx={{ color: '#424242', fontWeight: 500 }}>
                        {dayjs(timbratura.timestamp).tz('Europe/Rome').format('DD/MM/YYYY HH:mm')} - {timbratura.user_nome}
                      </Typography>

                      {/* Mostra indirizzo e città se ha GPS */}
                      {hasGPS && (
                        <GPSAddressDisplay
                          lat={timbratura.latitudine}
                          lon={timbratura.longitudine}
                          timbratureId={timbratura.id}
                          getAddress={getAddressFromCoordinates}
                          distance={distanceFromSite}
                        />
                      )}

                      {timbratura.note && (
                        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                          Note: {timbratura.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nessuna timbratura registrata oggi
          </Typography>
        )}
      </Paper>

      {/* Dialog Timbratura */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTipo && tipiTimbratura.find(t => t.id === selectedTipo)?.label}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {selectedTipo && tipiTimbratura.find((t) => t.id === selectedTipo)?.needsGPS && !gpsErrore && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <MyLocationIcon />
                Verrà registrata anche la posizione GPS, se disponibile
              </Box>
            </Alert>
          )}

          {/* Il GPS non è riuscito: si avvisa e si lascia decidere, senza impedire
              di registrare l'orario di lavoro. */}
          {gpsErrore && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={gpsLoading}
                  onClick={async () => {
                    setGpsErrore('');
                    const esito = await ottieniPosizione();
                    if (esito.posizione) {
                      setCurrentPosition(esito.posizione);
                      setTimbraSenzaGps(false);
                    } else {
                      setGpsErrore(esito.errore || 'Posizione non disponibile.');
                    }
                  }}
                >
                  Riprova
                </Button>
              }
            >
              <strong>{gpsErrore}</strong>
              <br />
              La timbratura verrà registrata ugualmente, senza coordinate.
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <GroupIcon />
              Questa timbratura sarà registrata per tutto il team presente
            </Box>
          </Alert>

          <TextField
            label="Note (opzionale)"
            multiline
            rows={3}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 2 }}
          />

          {currentPosition && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Posizione acquisita: {currentPosition.latitude.toFixed(6)}, {currentPosition.longitude.toFixed(6)}
              {currentPosition.accuratezza
                ? ` (precisione ±${Math.round(currentPosition.accuratezza)} m)`
                : ''}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button
            onClick={handleTimbratura}
            variant="contained"
            color={timbraSenzaGps ? 'warning' : 'primary'}
            disabled={gpsLoading}
            startIcon={gpsLoading ? <CircularProgress size={20} /> : <AccessTimeIcon />}
          >
            {gpsLoading
              ? 'Rilevamento posizione…'
              : timbraSenzaGps
              ? 'Timbra comunque'
              : 'Conferma Timbratura'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Gestione Presenze */}
      <Dialog open={presenzeDialogOpen} onClose={() => setPresenzeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gestione Presenze Team</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Indica chi è presente per questo intervento:
          </Typography>

          <FormGroup>
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPresenze[`capocantiere-${intervento?.capocantiere_id}`] || false}
                    onChange={(e) => handlePresenzeChange(`capocantiere-${intervento?.capocantiere_id}`, e.target.checked)}
                  />
                }
                label={`${intervento.capocantiere} (Capocantiere)`}
              />
              {!selectedPresenze[`capocantiere-${intervento?.capocantiere_id}`] && (
                <TextField
                  select
                  label="Motivo Assenza"
                  fullWidth
                  size="small"
                  value={motiviAssenza[`capocantiere-${intervento?.capocantiere_id}`] || ''}
                  onChange={(e) => setMotiviAssenza(prev => ({
                    ...prev,
                    [`capocantiere-${intervento?.capocantiere_id}`]: e.target.value
                  }))}
                  sx={{ mt: 1, ml: 4 }}
                >
                  <MenuItem value="malattia">Malattia</MenuItem>
                  <MenuItem value="ferie">Ferie</MenuItem>
                  <MenuItem value="permesso">Permesso</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </TextField>
              )}
            </Box>

            {intervento.tecnici?.map((tecnico) => (
              <Box key={tecnico.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPresenze[`tecnico-${tecnico.id}`] || false}
                      onChange={(e) => handlePresenzeChange(`tecnico-${tecnico.id}`, e.target.checked)}
                    />
                  }
                  label={tecnico.nome}
                />
                {!selectedPresenze[`tecnico-${tecnico.id}`] && (
                  <TextField
                    select
                    label="Motivo Assenza"
                    fullWidth
                    size="small"
                    value={motiviAssenza[`tecnico-${tecnico.id}`] || ''}
                    onChange={(e) => setMotiviAssenza(prev => ({
                      ...prev,
                      [`tecnico-${tecnico.id}`]: e.target.value
                    }))}
                    sx={{ mt: 1, ml: 4 }}
                  >
                    <MenuItem value="malattia">Malattia</MenuItem>
                    <MenuItem value="ferie">Ferie</MenuItem>
                    <MenuItem value="permesso">Permesso</MenuItem>
                    <MenuItem value="altro">Altro</MenuItem>
                  </TextField>
                )}
              </Box>
            ))}
          </FormGroup>

          <Alert severity="info" sx={{ mt: 2 }}>
            Per ogni persona assente, specifica il motivo individuale dell'assenza
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresenzeDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" color="primary" onClick={handleSalvaPresenze}>
            Salva Presenze
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Uscita Anticipata */}
      <Dialog open={uscitaAnticipataDialog} onClose={() => setUscitaAnticipataDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registra Uscita Anticipata</DialogTitle>
        <DialogContent>
          {selectedPersonaUscita && (
            <Box mb={2}>
              <Typography variant="h6">
                {selectedPersonaUscita.user_nome} ({selectedPersonaUscita.ruolo})
              </Typography>
            </Box>
          )}

          <TextField
            label="Motivo Uscita Anticipata"
            fullWidth
            multiline
            rows={3}
            value={motivoUscitaAnticipata}
            onChange={(e) => setMotivoUscitaAnticipata(e.target.value)}
            placeholder="Es: Appuntamento medico, emergenza familiare, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUscitaAnticipataDialog(false)}>Annulla</Button>
          <Button
            onClick={handleConfirmUscitaAnticipata}
            variant="contained"
            color="warning"
            disabled={!motivoUscitaAnticipata.trim()}
          >
            Conferma Uscita
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={orariDialog} onClose={() => setOrariDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Orario personalizzato</DialogTitle>
        <DialogContent>
          {selectedPersonaOrari && (
            <Box mb={2}>
              <Typography variant="h6">
                {selectedPersonaOrari.user_nome} ({selectedPersonaOrari.ruolo})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Imposta entrata e uscita reali quando differiscono dall'orario della squadra.
                Lascia vuoto un campo per usare l'orario di squadra.
              </Typography>
            </Box>
          )}
          <Box display="flex" gap={2} mt={1}>
            <TimePicker
              label="Entrata"
              value={entrataVal}
              onChange={setEntrataVal}
              ampm={false}
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
            <TimePicker
              label="Uscita"
              value={uscitaVal}
              onChange={setUscitaVal}
              ampm={false}
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrariDialog(false)}>Annulla</Button>
          <Button onClick={handleConfirmOrari} variant="contained" color="primary">
            Salva Orari
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Rapportino di Lavoro */}
      <RapportinoModal
        open={rapportinoModalOpen}
        onClose={() => setRapportinoModalOpen(false)}
        intervento={intervento}
        timbrature={timbrature}
        presenze={presenze}
        onSuccess={() => {
          setRapportinoModalOpen(false);
          loadData();
          alert('Rapportino di lavoro creato con successo!');
        }}
      />

      {/* Dialog Visualizzazione Rapportino */}
      <Dialog
        open={viewRapportinoDialog}
        onClose={() => setViewRapportinoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DescriptionIcon />
            Rapportino di Lavoro
          </Box>
        </DialogTitle>
        <DialogContent>
          {rapportino && (
            <>
              {/* Informazioni Intervento */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  {intervento?.codice} - {intervento?.cantiere}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Cliente</Typography>
                    <Typography variant="body1">{intervento?.cliente}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Data</Typography>
                    <Typography variant="body1">
                      {rapportino.data ? dayjs(rapportino.data).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* Orari */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon />
                  Orari e Tempi
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fase</TableCell>
                        <TableCell align="right">Orario</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rapportino.ora_partenza_sede && (
                        <TableRow>
                          <TableCell>Partenza Sede</TableCell>
                          <TableCell align="right">{rapportino.ora_partenza_sede.substring(0, 5)}</TableCell>
                        </TableRow>
                      )}
                      {(rapportino.ora_arrivo_cantiere || rapportino.ora_inizio_lavoro) && (
                        <TableRow>
                          <TableCell>Arrivo Cantiere / Inizio Lavoro</TableCell>
                          <TableCell align="right">{(rapportino.ora_arrivo_cantiere || rapportino.ora_inizio_lavoro).substring(0, 5)}</TableCell>
                        </TableRow>
                      )}
                      {rapportino.ora_fine_lavoro && (
                        <TableRow>
                          <TableCell>Fine Lavoro</TableCell>
                          <TableCell align="right">{rapportino.ora_fine_lavoro.substring(0, 5)}</TableCell>
                        </TableRow>
                      )}
                      {rapportino.ora_partenza_cantiere && (
                        <TableRow>
                          <TableCell>Partenza Cantiere</TableCell>
                          <TableCell align="right">{rapportino.ora_partenza_cantiere.substring(0, 5)}</TableCell>
                        </TableRow>
                      )}
                      {rapportino.ora_arrivo_sede && (
                        <TableRow>
                          <TableCell>Arrivo Sede</TableCell>
                          <TableCell align="right">{rapportino.ora_arrivo_sede.substring(0, 5)}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell><strong>Ore Lavorate</strong></TableCell>
                        <TableCell align="right"><strong>{rapportino.ore_lavorate?.toFixed(2) || 0} ore</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Km Percorsi (stima)</strong></TableCell>
                        <TableCell align="right"><strong>{rapportino.km_percorsi || 0} km</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Team - Presenze e Assenze */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon />
                  Team
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Presenti
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                      {presenze.filter(p => p.presente && !p.uscita_anticipata).map((p, idx) => (
                        <Chip
                          key={idx}
                          label={`${p.user_nome} (${p.ruolo})`}
                          color="success"
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                      {presenze.filter(p => p.presente && !p.uscita_anticipata).length === 0 && (
                        <Typography variant="body2" color="text.secondary">Nessuno presente</Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Assenti
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
                      {presenze.filter(p => !p.presente).map((p, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Chip
                            label={`${p.user_nome}: ${p.motivo_assenza}`}
                            color="error"
                            size="small"
                          />
                        </Box>
                      ))}
                      {presenze.filter(p => !p.presente).length === 0 && (
                        <Typography variant="body2" color="text.secondary">Nessuno assente</Typography>
                      )}
                    </Paper>
                  </Grid>
                  {presenze.filter(p => p.uscita_anticipata).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        Uscite Anticipate
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                        {presenze.filter(p => p.uscita_anticipata).map((p, idx) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Chip
                              label={`${p.user_nome}: ${p.motivo_uscita_anticipata}`}
                              color="warning"
                              size="small"
                            />
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Task Svolte */}
              {rapportino.tasks_svolte && rapportino.tasks_svolte.length > 0 ? (
                <>
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#4caf50' }} />
                      Task Svolte Durante l'Intervento
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Descrizione Task</TableCell>
                            <TableCell align="center">Quantità Svolta</TableCell>
                            <TableCell align="center">Totale Completata</TableCell>
                            <TableCell align="center">Stato</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rapportino.tasks_svolte.map((task, idx) => {
                            const percentuale = task.quantita > 0
                              ? Math.round((task.quantita_completata / task.quantita) * 100)
                              : 0;
                            const isCompleted = task.completata;

                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {isCompleted ? (
                                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                                    ) : (
                                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #9e9e9e' }} />
                                    )}
                                    <Typography variant="body2">{task.descrizione}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={task.quantita_oggi || 0}
                                    size="small"
                                    color="primary"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${task.quantita_completata || 0} / ${task.quantita}`}
                                    size="small"
                                    color={isCompleted ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={percentuale}
                                      sx={{
                                        flex: 1,
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: isCompleted ? '#4caf50' : '#2196f3',
                                        },
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ minWidth: 40 }}>
                                      {percentuale}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </>
              ) : (
                <Box mb={3}>
                  <Alert severity="info">Nessuna task è stata registrata per questo intervento</Alert>
                </Box>
              )}

              {/* Descrizione */}
              {rapportino.descrizione_intervento && rapportino.descrizione_intervento.trim() && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Descrizione Lavori
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {rapportino.descrizione_intervento}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Materiali */}
              {rapportino.materiali_utilizzati && rapportino.materiali_utilizzati.trim() && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Materiali Utilizzati
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {rapportino.materiali_utilizzati}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Note */}
              {rapportino.note && rapportino.note.trim() && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Note Aggiuntive
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#fff9c4', border: '1px solid #fbc02d' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {rapportino.note}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Firma */}
              {rapportino.firma_cliente && intervento?.tipologia === 'manutenzione' && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Firma Cliente: {rapportino.nome_firmatario}
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <img
                      src={rapportino.firma_cliente}
                      alt="Firma Cliente"
                      style={{ maxWidth: '100%', border: '1px solid #ddd' }}
                    />
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRapportinoDialog(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Timbrature;